import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Popup, CustomTable, Loading, Button } from 'src/components'
import { ConfirmationModal } from 'src/components/ConfirmationModal'
import { PageTitle } from 'src/components/PageTitle'
import { Filter } from 'src/assets/svg'
import { more } from 'src/assets/png'
import { useFilterToggler } from 'src/components/CustomTable/hooks/useFilterToggler'
import { useGetFarmList } from 'src/hooks/api/farm'
import { useAuth } from 'src/contexts/auth'
import { FarmDto } from 'src/dto/farm.dto'
import ROUTES from 'src/constants/routes'
import { usePropertiesTableHeader } from './hooks/usePropertiesTableHeader'
import UpdateProperties from './UpdateProperties'
import { useStyles } from './styles'

export const Properties: React.FC = () => {
  const style = useStyles()
  const navigate = useNavigate()
  const { switchFarm } = useAuth()
  const [farmData, setFarmData] = useState<FarmDto | undefined | null>(null)
  const [pendingFarmId, setPendingFarmId] = useState<string | null>(null)
  const { filterIsActive, handleFilter } = useFilterToggler()

  const { data: propertiesList = [], isLoading } = useGetFarmList()

  const handleItemDetailModal = useCallback((item: FarmDto | undefined | null) => {
    setFarmData(item)
  }, [])

  const handleClose = useCallback(() => {
    setFarmData(null)
  }, [])

  const handleSelectFarm = useCallback((item: FarmDto) => {
    if (item.idFazenda) {
      setPendingFarmId(item.idFazenda)
    }
  }, [])

  const handleConfirmSwitch = useCallback(() => {
    if (pendingFarmId) {
      switchFarm(pendingFarmId)
      setPendingFarmId(null)
      navigate(ROUTES.DASHBOARD)
    }
  }, [pendingFarmId, switchFarm, navigate])

  const handleCancelSwitch = useCallback(() => {
    setPendingFarmId(null)
  }, [])

  const { tableHeader } = usePropertiesTableHeader({ handleItemDetailModal, handleSelectFarm })

  const renderContent = () => {
    if (isLoading) return <Loading />

    return (
      <div className={style.propertiesContainer}>
        <CustomTable
          header={tableHeader}
          filterIsActive={filterIsActive}
          body={propertiesList}
          propertiesToFilter={['descricao', 'razao', 'documento', 'proprietario', 'cidade', 'estado']}
          filterPlaceholder='Nome, CNPJ, Cidade...'
        />
      </div>
    )
  }

  return (
    <>
      <div className={style.propertiesHeader}>
        <div className={style.propertiesHeaderContent}>
          <PageTitle title='Propriedades' />
          <div className={style.buttonContainer}>
            <Button secondary disabled={!propertiesList.length} onClick={handleFilter} outlined>
              <Filter className='icon' />
              FILTRAR
            </Button>

            <Button onClick={() => handleItemDetailModal(undefined)}>
              <img src={more} alt='Incluir' />
              CADASTRAR
            </Button>
          </div>
        </div>
      </div>

      {renderContent()}

      {farmData !== null && (
        <Popup
          className={style.propertiesPopup}
          title={farmData ? 'Dados da Propriedade' : 'Cadastrar nova propriedade'}
          onClose={handleClose}
          closeOnClickOutside={false}
        >
          <UpdateProperties value={farmData || undefined} onClose={handleClose} />
        </Popup>
      )}

      <ConfirmationModal
        isOpen={!!pendingFarmId}
        title='Trocar de propriedade'
        message='Ao trocar de propriedade, todos os dados serão carregados de acordo com a nova propriedade selecionada.'
        onConfirm={handleConfirmSwitch}
        onCancel={handleCancelSwitch}
        confirmText='Confirmar'
        cancelText='Cancelar'
      />
    </>
  )
}
